import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';
import { usePublish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { type OpenRovoRemixSidebarPayload, Topics } from '@atlaskit/rovo-triggers/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { isEmptyAdf } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import {
	expVal,
	isExperimentEnabled,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { LoadableLazy } from '@confluence/loadable';
import {
	type ObjectSidebarRemixLifecycleAdapter,
	useObjectSidebarRemixLifecycle,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import { useObjectSidebarRemixLifecycleDiagnostics } from '@confluence/malleable-ui-common/entry-points/useObjectSidebarRemixLifecycleDiagnostics.ts';
import { type PageRemixCloseOptions } from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { prepareObjectSidebarPageRemixStart } from '@confluence/malleable-ui-common/entry-points/ObjectSidebarPageRemixStart.ts';
import { useSpaceRemixOptionsPrefetch } from '@confluence/malleable-ui-common/entry-points/useShouldHideDiagramRemixOption.ts';
import { subscribeToObjectSidebarRemixPickerClose } from '@confluence/malleable-ui-common/entry-points/objectSidebarRemixPickerClose.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { useObjectSidebarRemixLifecycleSession } from './ObjectSidebarRemixLifecycleSession.tsx';
import { parseObjectSidebarRemixAdf } from './objectSidebarRemixAdf.ts';

export const OBJECT_LEVEL_REMIX_ENTRY_POINT = 'objectSidebar';
export const OBJECT_LEVEL_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';

const remixObjectPagePopupLoader = () =>
	import(/* webpackChunkName: "loadable-RemixObjectPagePopup" */ './RemixObjectPagePopup.tsx');

const RemixObjectPagePopup = LoadableLazy({
	loader: async () => (await remixObjectPagePopupLoader()).RemixObjectPagePopup,
});

const remixButtonContainerStyle = css({
	position: 'relative',
});

const popupTriggerOverlayStyle = css({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
});

const placeholderButtonStyle = css({
	visibility: 'hidden',
});

const i18n = defineMessages({
	remixPageLabel: {
		id: 'object-sidebar-components.remix-page.label',
		defaultMessage: 'Remix page',
		description: 'Label for the button that opens the page-level remix overlay',
	},
	remixPageTooltip: {
		id: 'object-sidebar-components.remix-page.tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the button that opens the object-level remix overlay',
	},
	remixPageEmptyTooltip: {
		id: 'object-sidebar-components.remix-page.empty-tooltip',
		defaultMessage: 'Add content to this page to remix',
		description: 'Tooltip shown when the remix button is disabled because the page has no content',
	},
	remixInProgressTooltip: {
		id: 'object-sidebar-components.remix-page.in-progress-tooltip',
		defaultMessage: 'Remix in progress',
		description:
			'Tooltip shown when the remix button is disabled because a remix is already in progress',
	},
});

type RemixPanelButtonProps = {
	contentId?: string;
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
	isEditorEmpty?: boolean;
	isDisabled?: boolean;
	lifecycleAdapter?: ObjectSidebarRemixLifecycleAdapter;
};

export const RemixPanelButton = ({
	contentId,
	contentBodyValue,
	getAdfDocument,
	isEditorEmpty,
	isDisabled = false,
}: RemixPanelButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { isLoggedIn, isRovoLLMEnabled, accessStatus } = useSessionData();
	// Match App.jsx: the Rovo submit path only exists when Rovo chat is allowed for this user.
	// The lifecycle session applies the same guard before hosting the coordinator, so that this
	// button and the coordinator agree on which path a click starts.
	const isRovoEnabled =
		isRovoLLMEnabled && isLoggedIn && accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;
	const shouldUseRovoRemixPath =
		isRovoEnabled &&
		UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
	const { didPrefetch: didPrefetchSpaceOptions, hiddenOptions: prefetchedSpaceHiddenOptions } =
		useSpaceRemixOptionsPrefetch({
			// The modal records the permissions exposure when opened; prefetching must not record it.
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			enabled: UNSAFE_expValEqualsNoExposure(
				'cc_maui_remix_attachment_permissions',
				'isEnabled',
				true,
			),
		});
	const { showSmartCreationModal } = useSmartCreationModalState();
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const experienceTracker = useAIMateExperienceTracker();
	const publishOpenRovoRemix = usePublish(Topics.AI_MATE);
	const publishRovoRemixLaunch = usePublish(Topics.ROVO_REMIX_LAUNCH);
	const { openConversationAssistant, isConversationAssistantSidebarActive, mode } =
		useToggleConversationAssistant();
	const {
		admitObjectSidebarRemixLifecycle,
		clearRovoRemixStarted,
		closeObjectSidebarRemixModal,
		hasAdapter: shouldAttemptLifecycleAdmission,
		isObjectSidebarRemixModalOpen,
		isRemixStartBlockedByActiveFlow,
		isRovoRemixStarted,
		markRovoRemixStarted,
		openObjectSidebarRemixModal,
		registerPopupAnchor,
		releaseRetainedLifecycleForToken,
		retainedLifecycleToken,
	} = useObjectSidebarRemixLifecycleSession();
	// `handleOpenSidebarConversation` is invoked asynchronously from the remix flow
	// (after `sendAIFCPrompt` resolves), by which point the sidebar's active state may
	// have changed. Track the latest value in a ref so the guard reads the current
	// value at invocation time rather than a stale value captured at render time.
	// Mirrors the existing async-safe ref pattern (e.g. `isRemixCancelled`).
	const isConversationAssistantSidebarActiveRef = useRef(isConversationAssistantSidebarActive);
	isConversationAssistantSidebarActiveRef.current = isConversationAssistantSidebarActive;

	const handleOpenSidebarConversation = useCallback(
		(conversationId: string) => {
			updatePageRovoParams({ pathway: 'chat', conversationId });
			if (!isConversationAssistantSidebarActiveRef.current) {
				openConversationAssistant('sidebar');
			}
		},
		[openConversationAssistant],
	);

	const isRemixStartActive = useRef(false);
	// Set when this button asked the Rovo panel to open. Kept local rather than reading the shared
	// `isRovoRemixStarted`, because the session provider clears that flag on the same panel close
	// this effect reacts to, and whichever effect ran first would otherwise decide whether the
	// experience gets aborted.
	const hasStartedRovoRemixRef = useRef(false);
	const hasOpenedRemixModalRef = useRef(false);
	const startRequestIdRef = useRef(0);
	const adfSourceVersionRef = useRef(0);
	const lifecycleAdfSourceRef = useRef({ contentBodyValue, getAdfDocument });
	const isLifecycleTreatmentActiveRef = useRef(false);
	useEffect(() => {
		if (!isLifecycleTreatmentActiveRef.current) {
			return;
		}
		lifecycleAdfSourceRef.current = { contentBodyValue, getAdfDocument };
		adfSourceVersionRef.current += 1;
	}, [contentBodyValue, getAdfDocument]);
	useEffect(
		() => () => {
			if (!isLifecycleTreatmentActiveRef.current) {
				return;
			}
			adfSourceVersionRef.current += 1;
			startRequestIdRef.current += 1;
		},
		[],
	);

	const [isModalOpen, setIsModalOpen] = useState(false);
	// With this sidebar's own popup open, the button is that popup's close toggle (a dismiss control),
	// so another active flow only blocks new starts while the popup is closed. Once this sidebar's own
	// flow is admitted, the popup has given way to the thinking bar, so the button is no longer its
	// close toggle even while the popup session is still open (a deferred custom prompt keeps it open
	// until the reply is ready).
	const { isAdmitted: isOwnFlowAdmitted } = useObjectSidebarRemixLifecycle();
	const isNewStartBlocked =
		isRemixStartBlockedByActiveFlow &&
		(!(isModalOpen || isObjectSidebarRemixModalOpen) || isOwnFlowAdmitted);
	const [lazyAdfDocument, setLazyAdfDocument] = useState<DocNode | null>(null);
	const [viewOpenAdfDocument, setViewOpenAdfDocument] = useState<DocNode | null>(null);

	const hasContentFromBody = useMemo(() => {
		if (getAdfDocument) {
			return false;
		}
		if (!contentBodyValue) {
			return false;
		}
		const parsed = parseObjectSidebarRemixAdf(contentBodyValue);
		return parsed !== null && !isEmptyAdf(parsed);
	}, [contentBodyValue, getAdfDocument]);

	const hasContent = getAdfDocument ? !isEditorEmpty : hasContentFromBody;

	// Measures treatment users who see an otherwise usable button disabled by another flow before
	// they click it. They cannot click, so the click exposure never counts them. A click in this
	// mount has already recorded the exposure, so a block after it (such as the user's own flow
	// being admitted) is not reported, and neither is a block both groups share (no content, the
	// parent disabling it, or the Smart Creation modal). Once per mount; records no exposure.
	const hasClickedRef = useRef(false);
	const hasReportedBlockedButtonRef = useRef(false);
	useEffect(() => {
		if (
			hasReportedBlockedButtonRef.current ||
			hasClickedRef.current ||
			isDisabled ||
			!hasContent ||
			showSmartCreationModal ||
			!isNewStartBlocked
		) {
			return;
		}
		hasReportedBlockedButtonRef.current = true;
		reportLifecycleDiagnostic('observed', {
			operation: 'remixButtonBlocked',
			entryPath: shouldUseRovoRemixPath ? 'rovo-sidebar' : 'popup-open',
		});
	}, [
		hasContent,
		isDisabled,
		isNewStartBlocked,
		reportLifecycleDiagnostic,
		shouldUseRovoRemixPath,
		showSmartCreationModal,
	]);

	const resolvedAdfDocument = getAdfDocument ? lazyAdfDocument : viewOpenAdfDocument;

	const loadAdfDocument = useCallback(async () => {
		if (getAdfDocument) {
			return getAdfDocument();
		}
		if (!contentBodyValue) {
			return null;
		}
		return parseObjectSidebarRemixAdf(contentBodyValue);
	}, [getAdfDocument, contentBodyValue]);

	const loadLifecycleTreatmentAdfDocument = useCallback(async () => {
		const adfSourceVersion = adfSourceVersionRef.current;
		const { contentBodyValue: committedContentBodyValue, getAdfDocument: currentGetAdfDocument } =
			lifecycleAdfSourceRef.current;
		const adfDocument = currentGetAdfDocument
			? await currentGetAdfDocument()
			: committedContentBodyValue
				? parseObjectSidebarRemixAdf(committedContentBodyValue)
				: null;
		return adfSourceVersionRef.current === adfSourceVersion ? adfDocument : null;
	}, []);

	const [isLoadingChunk, setIsLoadingChunk] = useState(false);
	// Tracks whether the popup has ever been activated so the lazy chunk is loaded
	const [hasBeenActivated, setHasBeenActivated] = useState(false);

	// Preload the heavy popup chunk on hover/focus so it's ready by the time the user clicks
	const handlePreload = useCallback(() => {
		void remixObjectPagePopupLoader();
	}, []);

	const clearRemixUiState = useCallback(() => {
		setIsModalOpen(false);
		setViewOpenAdfDocument(null);
		setLazyAdfDocument(null);
		clearRovoRemixStarted();
		hasStartedRovoRemixRef.current = false;
		isRemixStartActive.current = false;
	}, [clearRovoRemixStarted]);

	const closeSessionModal = useCallback(
		(options?: PageRemixCloseOptions) => {
			closeObjectSidebarRemixModal(options);
			clearRemixUiState();
		},
		[clearRemixUiState, closeObjectSidebarRemixModal],
	);
	useEffect(() => {
		if (
			!contentId ||
			!isModalOpen ||
			isOwnFlowAdmitted ||
			!UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-close-object-sidebar-on-selection-remix',
				'isEnabled',
				true,
			)
		) {
			return;
		}
		return subscribeToObjectSidebarRemixPickerClose((requestedContentId: string) => {
			if (requestedContentId === contentId) {
				clearRemixUiState();
			}
		});
	}, [clearRemixUiState, contentId, isModalOpen, isOwnFlowAdmitted]);

	const fireRemixButtonAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				source: 'remixModalFromObjectSidebar',
				attributes: {
					entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleClick = useCallback(async () => {
		if (!hasContent || showSmartCreationModal) {
			return;
		}

		// Both lifecycle groups reach this point, so read the cohort here, which records the
		// exposure, before the treatment-only block below. A blocked treatment button is disabled and
		// never gets here, so that case is measured by the `remixButtonBlocked` diagnostic instead.
		const isLifecycleExperimentActive =
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('remix_state_rearchitecture_enabled') &&
			isExperimentEnabled('remix_state_rearchitecture_object_sidebar');
		hasClickedRef.current = true;
		if (isNewStartBlocked) {
			return;
		}

		// Record the Remix menu experiment exposure here, where the cohort decides which Remix
		// picker the person is about to see. Every cohort reaches this point, so all three are
		// counted at the same moment in the journey. Recording it further downstream, once each
		// cohort's own picker had appeared, left the variant2 group short, because their picker
		// only appears if the Rovo side panel is listening by the time this button asks it to
		// open.
		expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		const isAnyPageRemixModalOpen = isObjectSidebarRemixModalOpen || isModalOpen;

		if (shouldAttemptLifecycleAdmission) {
			if (!shouldUseRovoRemixPath && (isAnyPageRemixModalOpen || isLoadingChunk)) {
				if (isObjectSidebarRemixModalOpen) {
					closeSessionModal();
				} else {
					clearRemixUiState();
				}
				return;
			}
		}

		if (shouldUseRovoRemixPath) {
			if (!isRemixStartActive.current) {
				const startAttributes = {
					entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
					remixSource: OBJECT_LEVEL_REMIX_SOURCE,
					product: 'confluence',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: startAttributes,
				});
				startRemixCreateExperience({
					experienceTracker,
					attributes: startAttributes,
				});
				isRemixStartActive.current = true;
			}

			const payload: OpenRovoRemixSidebarPayload = {
				type: 'open-rovo-remix-sidebar',
				source: OBJECT_LEVEL_REMIX_SOURCE,
				consumeOnce: true,
				data: {
					remixConsumer: 'renderer',
					remixPreset: 'page',
				},
			};
			publishOpenRovoRemix(payload);
			publishRovoRemixLaunch(payload);
			markRovoRemixStarted();
			hasStartedRovoRemixRef.current = true;
			if (!isConversationAssistantSidebarActiveRef.current) {
				openConversationAssistant('sidebar');
			}
			fireRemixButtonAnalytics();
			return;
		}

		if (!shouldAttemptLifecycleAdmission && (isAnyPageRemixModalOpen || isLoadingChunk)) {
			clearRemixUiState();
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				remixSource: OBJECT_LEVEL_REMIX_SOURCE,
				product: 'confluence',
			},
		});
		startRemixCreateExperience({
			experienceTracker,
			attributes: {
				entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				remixSource: OBJECT_LEVEL_REMIX_SOURCE,
				product: 'confluence',
			},
		});
		isRemixStartActive.current = true;
		isLifecycleTreatmentActiveRef.current = isLifecycleExperimentActive;
		if (isLifecycleExperimentActive) {
			lifecycleAdfSourceRef.current = { contentBodyValue, getAdfDocument };
			adfSourceVersionRef.current += 1;
		}
		const startRequestId = isLifecycleExperimentActive ? startRequestIdRef.current + 1 : undefined;
		if (startRequestId !== undefined) {
			startRequestIdRef.current = startRequestId;
		}
		const adfSourceVersion = isLifecycleExperimentActive ? adfSourceVersionRef.current : undefined;
		const isCurrentStart = () =>
			startRequestId !== undefined &&
			adfSourceVersion !== undefined &&
			startRequestIdRef.current === startRequestId &&
			adfSourceVersionRef.current === adfSourceVersion;
		// Ensure the popup chunk is loaded before opening — the hover preload
		// means this will typically resolve instantly from cache
		setIsLoadingChunk(true);
		await remixObjectPagePopupLoader();
		setIsLoadingChunk(false);

		if (!isLifecycleExperimentActive) {
			if (shouldAttemptLifecycleAdmission) {
				const doc = await loadAdfDocument();
				if (!doc || isEmptyAdf(doc)) {
					return;
				}

				const lifecycleAdmission = admitObjectSidebarRemixLifecycle();
				if (lifecycleAdmission === 'rejected') {
					return;
				}

				hasOpenedRemixModalRef.current = true;
				if (getAdfDocument) {
					setLazyAdfDocument(doc);
				} else {
					setViewOpenAdfDocument(doc);
				}
				setIsModalOpen(true);
				setHasBeenActivated(true);
			} else if (getAdfDocument) {
				const doc = await getAdfDocument();
				if (doc) {
					setLazyAdfDocument(doc);
					hasOpenedRemixModalRef.current = true;
					setIsModalOpen(true);
					setHasBeenActivated(true);
				}
			} else if (contentBodyValue) {
				const doc = parseObjectSidebarRemixAdf(contentBodyValue);
				if (doc) {
					setViewOpenAdfDocument(doc);
					hasOpenedRemixModalRef.current = true;
					setIsModalOpen(true);
					setHasBeenActivated(true);
				}
			}
			fireRemixButtonAnalytics();
			return;
		}

		if (!isCurrentStart()) {
			return;
		}

		const startResult = await prepareObjectSidebarPageRemixStart({
			resolveAdfDocument: loadLifecycleTreatmentAdfDocument,
			isCurrent: isCurrentStart,
			onRejected: (reason) => {
				reportLifecycleDiagnostic('rejected', {
					operation: 'preparePageRemixStart',
					entryPath: 'popup-open',
					rejectionReason: reason,
				});
			},
		});

		if (startResult.status === 'rejected') {
			return;
		}

		hasOpenedRemixModalRef.current = true;
		openObjectSidebarRemixModal(startResult.adfDocument, loadLifecycleTreatmentAdfDocument, {
			isLifecycleTreatment: isLifecycleExperimentActive,
		});
		fireRemixButtonAnalytics();
	}, [
		experienceTracker,
		hasContent,
		isModalOpen,
		isObjectSidebarRemixModalOpen,
		isNewStartBlocked,
		showSmartCreationModal,
		clearRemixUiState,
		closeSessionModal,
		isLoadingChunk,
		getAdfDocument,
		contentBodyValue,
		loadAdfDocument,
		loadLifecycleTreatmentAdfDocument,
		publishOpenRovoRemix,
		publishRovoRemixLaunch,
		openConversationAssistant,
		fireRemixButtonAnalytics,
		markRovoRemixStarted,
		shouldUseRovoRemixPath,
		shouldAttemptLifecycleAdmission,
		admitObjectSidebarRemixLifecycle,
		openObjectSidebarRemixModal,
		reportLifecycleDiagnostic,
	]);

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	const abortActiveRemixStart = useCallback(
		(reason: string, options: { releaseLifecycle?: boolean } = {}) => {
			if (!isRemixStartActive.current) {
				return;
			}
			if (options.releaseLifecycle !== false) {
				releaseRetainedLifecycleForToken(retainedLifecycleToken, 'abandoned');
			}
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason,
				attributes: { reason },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasOpenedRemixModalRef.current,
			);
			isRemixStartActive.current = false;
		},
		[releaseRetainedLifecycleForToken, retainedLifecycleToken],
	);
	useEffect(() => {
		return () => abortActiveRemixStart('User closed remix overlay', { releaseLifecycle: false });
	}, [abortActiveRemixStart]);

	const onLocalModalStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			clearRovoRemixStarted();
			if (options?.reopenModal) {
				hasOpenedRemixModalRef.current = true;
				setIsModalOpen(true);
			}
		},
		[clearRovoRemixStarted],
	);

	// Closing the Rovo panel sends no message that this button can listen for, so the only way to
	// notice it is to watch whether the panel is showing. The same click that starts the Remix also
	// asks the panel to open, so there is a short moment where the panel is not showing yet. A close
	// is therefore only acted on once the panel has been seen showing at least once.
	const isRovoPanelOpen = isConversationAssistantSidebarActive || mode !== undefined;
	const hasSeenRovoPanelOpenRef = useRef(false);
	useEffect(() => {
		if (isRovoPanelOpen) {
			hasSeenRovoPanelOpenRef.current = true;
			return;
		}
		if (!hasSeenRovoPanelOpenRef.current) {
			return;
		}
		hasSeenRovoPanelOpenRef.current = false;
		// Only the experience abort belongs here. Clearing the started flag is the session
		// provider's job, because it has to happen even when this button is unmounted.
		if (hasStartedRovoRemixRef.current) {
			hasStartedRovoRemixRef.current = false;
			abortActiveRemixStart('User closed remix overlay');
		}
	}, [abortActiveRemixStart, isRovoPanelOpen]);

	// Another Remix entry point taking over. This button asks for a Remix of the whole page, while
	// the text selection toolbar and the block hover icon ask for a Remix of the selection, so an
	// announcement for anything other than the page means one of those has taken over.
	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type !== 'open-rovo-remix-sidebar' || payload.data.remixPreset === 'page') {
			return;
		}
		hasStartedRovoRemixRef.current = false;
		abortActiveRemixStart('Another Remix entry point took over');
		clearRovoRemixStarted();
	});

	const remixPageLabelMessage = i18n.remixPageLabel;

	const hasContentTooltipMessage = i18n.remixPageTooltip;

	const inProgressTooltipMessage = i18n.remixInProgressTooltip;

	const emptyTooltipMessage = i18n.remixPageEmptyTooltip;

	const shouldUseLifecycleCloseHandler = retainedLifecycleToken !== undefined;

	const isRemixInProgress = showSmartCreationModal || isNewStartBlocked;

	let tooltipContent: string | undefined;
	if (isRemixInProgress) {
		tooltipContent = formatMessage(inProgressTooltipMessage);
	} else if (isObjectSidebarRemixModalOpen || isModalOpen) {
		tooltipContent = undefined;
	} else if (hasContent) {
		tooltipContent = formatMessage(hasContentTooltipMessage);
	} else {
		tooltipContent = formatMessage(emptyTooltipMessage);
	}

	return (
		<div
			ref={registerPopupAnchor}
			css={remixButtonContainerStyle}
			data-testid="sidebar-remix-button-container"
			onMouseEnter={handlePreload}
			onFocus={handlePreload}
		>
			<Tooltip position="left" content={tooltipContent} hideTooltipOnClick>
				<IconButton
					onClick={handleClick}
					appearance="subtle"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					icon={AiGenerativeRemixIcon}
					label={formatMessage(remixPageLabelMessage)}
					testId="sidebar-remix-button"
					isLoading={isLoadingChunk}
					isDisabled={isDisabled || !hasContent || isRemixInProgress}
					// Selected while this button's Remix is showing: the variant2 Rovo panel (asked in the
					// form that does not record an exposure, because rendering must not enter anyone into
					// the experiment), or the popup the lifecycle treatment hosts in the session provider,
					// which has no overlay trigger of its own to show the selected state.
					isSelected={
						(isRovoRemixStarted && shouldUseRovoRemixPath) ||
						(isObjectSidebarRemixModalOpen && !showSmartCreationModal && !isNewStartBlocked)
					}
				/>
			</Tooltip>
			{/*
			 * The Rovo-sidebar submit coordinator is hosted by the lifecycle session provider, not
			 * here: this button unmounts whenever the Object Sidebar collapses, which would drop an
			 * armed Rovo submit.
			 */}
			{!shouldUseRovoRemixPath && hasBeenActivated && (
				<RemixObjectPagePopup
					adfDocument={resolvedAdfDocument ?? ({ type: 'doc', version: 1, content: [] } as DocNode)}
					isModalOpen={isModalOpen}
					isRemixStartBlocked={isRemixStartBlockedByActiveFlow}
					onClose={clearRemixUiState}
					onStopThinking={onLocalModalStopThinking}
					popupRepositionKey={rightSidebarWidth}
					onOpenSidebarConversation={handleOpenSidebarConversation}
					shouldWaitForRemixHandoffBeforeClose={shouldUseLifecycleCloseHandler}
					prefetchedSpaceHiddenOptions={
						didPrefetchSpaceOptions ? prefetchedSpaceHiddenOptions : undefined
					}
					trigger={(triggerProps) => (
						<div css={popupTriggerOverlayStyle}>
							<IconButton
								{...triggerProps}
								css={placeholderButtonStyle}
								appearance="subtle"
								shape={isRovoButtonEnabled ? 'default' : 'circle'}
								icon={AiGenerativeRemixIcon}
								label={formatMessage(remixPageLabelMessage)}
								testId="sidebar-remix-button"
								isDisabled={isDisabled || !hasContent || isRemixInProgress}
								isSelected={isModalOpen && !showSmartCreationModal}
								aria-hidden="true"
							/>
						</div>
					)}
				/>
			)}
		</div>
	);
};
